/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* istanbul ignore file */
import * as Survey from "survey-react";
import { format } from "date-fns";
import { whakapapaToSVG, WhakapapaRecord } from "@plinknz/tah-website-elements";
import {
  InteractionPayload,
  LinkPayload,
  MemberPayload,
  RegisterPayload,
  ChildrenPayload,
} from "../../config/forms/register/payload";

function pruneEmptyWhakapapa(data: WhakapapaRecord) {
  const newRec: WhakapapaRecord = { name: data.name };

  if (data.mother.name) {
    newRec.mother = pruneEmptyWhakapapa(data.mother);
  }

  if (data.father.name) {
    newRec.father = pruneEmptyWhakapapa(data.father);
  }

  return newRec;
}

function getGender(gender: string): string {
  if (gender === "Tāne") {
    return "Male";
  }
  if (gender === "Wahine") {
    return "Female";
  }
  return "";
}

export function transformRegistrationFormData({
  survey,
  tupuna,
  tupunaBaseId,
}: {
  survey: Survey.ReactSurveyModel;
  tupuna: string;
  tupunaBaseId: string;
}): RegisterPayload {
  const dateFormat = "yyyyMMdd";
  const tenant = process.env.TAH_TENANT;
  const surveyData = survey.data;
  const gender = getGender(surveyData.gender);
  const dob = format(new Date(surveyData.dob), dateFormat);
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  const data: MemberPayload = {
    title: surveyData.title,
    firstName: surveyData.firstName,
    middleNames: surveyData.middleNames,
    surname: surveyData.surname,
    gender,
    dob,
    cell: surveyData.cell,
    phone: surveyData.phone,
    email: surveyData.email,
    occupation: surveyData.occupation,
    line1: surveyData.addressfinder.address_line_1 || "",
    line2: surveyData.addressfinder.address_line_2 || "",
    suburb: surveyData.addressfinder.suburb || "",
    city: surveyData.addressfinder.city || "",
    postcode: surveyData.addressfinder.postcode || "",
    country: surveyData.addressfinder.country || "",
    tupunaBaseId,
    tupuna,
  };
  const interactions: InteractionPayload[] = [];
  const links: LinkPayload[] = [];
  const children: ChildrenPayload[] = [];

  ["education", "educationDetails", "birthplace", "whangai"].forEach(
    (attribute) => {
      if (surveyData[attribute]) {
        if (attribute !== "whangai" || surveyData.whangai !== "Kāo") {
          const capitalisedAttribute =
            attribute[0].toUpperCase() + attribute.slice(1);
          const value = `${capitalisedAttribute}: ${surveyData[attribute]}`;

          interactions.push({
            value,
          });
        }
      }
    }
  );

  let i = 1;
  let tamaiti = `tamaiti${i}`;

  while (surveyData[`${tamaiti}FirstName`]) {
    const firstName = surveyData[`${tamaiti}FirstName`];
    const middleNames = surveyData[`${tamaiti}MiddleNames`];
    const surname = surveyData[`${tamaiti}Surname`];
    const whangai = surveyData[`${tamaiti}Whangai`];
    const sdGender = getGender(surveyData[`${tamaiti}Gender`]);
    const dobAsDate = new Date(surveyData[`${tamaiti}Dob`]);
    const sdDob = format(dobAsDate, dateFormat);

    if (dobAsDate <= eighteenYearsAgo) {
      interactions.push({
        value: `Adult Tamaiti ${i} (not registered): ${firstName} ${middleNames} ${surname} (${sdGender}) Born ${sdDob}`,
      });
    } else {
      children.push({
        firstName,
        middleNames,
        surname,
        gender: sdGender,
        dob: sdDob,
      });
    }

    if (whangai === "Āe") {
      interactions.push({
        value: `${firstName} ${middleNames || ""} ${surname} is a whāngai`,
      });
    }

    i += 1;
    tamaiti = `tamaiti${i}`;
  }

  if (surveyData.signed) {
    links.push({
      type: "attachment",
      value: surveyData.signed,
    });
  }

  const name = `${data.firstName}${
    data.middleNames ? ` ${data.middleNames}` : ""
  } ${data.surname}`;
  const rawWhakapapa = { ...surveyData.whakapapa, name };
  const whakapapaData = pruneEmptyWhakapapa(rawWhakapapa);
  const whakapapaSvg = whakapapaToSVG(whakapapaData, "Registration Whakapapa");

  interactions.push({ value: whakapapaSvg });

  return { data, interactions, links, recaptcha: null, tenant, children };
}
